import { useImageAnnotatorContext } from "@msuite/image-annotator";
import type { DragKind } from "@msuite/utils/types";
import { Fragment, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
	AnnotationDragHandlePreview,
	type DragHandlePreviewRef,
	type PreviewViewLayout,
} from "./annotation-drag-handle-preview";
import { useImageCanvasContext } from "./context";

/** Props Interface */
interface AnnotationDragHandleProps {
	cx: number;
	cy: number;
	kind: DragKind;
	annotationId: string;
	index?: number;
}

function AnnotationDragHandleComponent({
	cx,
	cy,
	kind,
	annotationId,
	index,
}: AnnotationDragHandleProps) {
	/** Context */
	const context = useImageAnnotatorContext();
	const { containerRef, imageUrl } = useImageCanvasContext();
	const combinedScale = context.baseScale * context.scale;

	/** State */
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [viewLayout, setViewLayout] = useState<PreviewViewLayout | null>(null);

	/** Refs */
	const originalClientX = useRef(0);
	const originalClientY = useRef(0);
	const originalCx = useRef(cx);
	const originalCy = useRef(cy);
	const rafRef = useRef<number | null>(null);
	const pendingPosRef = useRef<{ x: number; y: number } | null>(null);
	const previewRef = useRef<DragHandlePreviewRef>(null);
	const viewLayoutRef = useRef<PreviewViewLayout | null>(null);

	/** Effects */
	useEffect(() => {
		return () => {
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
		};
	}, []);

	// Position the loupe at the initial handle location as soon as it mounts (before paint),
	// so it doesn't flash in the top-left corner before the first pointer move.
	useLayoutEffect(() => {
		if (!isDragging) return;
		const layout = viewLayoutRef.current;
		const anchorX = originalClientX.current;
		const anchorY = originalClientY.current;
		const focalX = layout ? layout.x + originalCx.current * combinedScale : anchorX;
		const focalY = layout ? layout.y + originalCy.current * combinedScale : anchorY;
		previewRef.current?.setPosition(anchorX, anchorY, focalX, focalY);
	}, [isDragging, combinedScale]);

	/** Functions */
	const flushDrag = useCallback(() => {
		rafRef.current = null;
		const pending = pendingPosRef.current;
		if (!pending) return;
		pendingPosRef.current = null;
		context.controller.handleOnDrag(annotationId, kind, pending, index);
	}, [annotationId, kind, index, context.controller]);

	function handleOnPointerDown(e: React.PointerEvent<SVGCircleElement>) {
		e.stopPropagation();
		e.currentTarget.setPointerCapture(e.pointerId);
		setIsDragging(true);

		originalClientX.current = e.clientX;
		originalClientY.current = e.clientY;
		originalCx.current = cx;
		originalCy.current = cy;

		const rect = containerRef.current?.getBoundingClientRect();
		if (rect) {
			const layout = { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
			viewLayoutRef.current = layout;
			setViewLayout(layout);
		}
	}

	function handleOnPointerMove(e: React.PointerEvent<SVGCircleElement>) {
		if (!isDragging) return;

		const deltaX = e.clientX - originalClientX.current;
		const deltaY = e.clientY - originalClientY.current;

		const layout = viewLayoutRef.current;
		const focalX = layout ? layout.x + originalCx.current * combinedScale + deltaX : e.clientX;
		const focalY = layout ? layout.y + originalCy.current * combinedScale + deltaY : e.clientY;

		previewRef.current?.setPosition(e.clientX, e.clientY, focalX, focalY);

		pendingPosRef.current = {
			x: originalCx.current + deltaX / combinedScale,
			y: originalCy.current + deltaY / combinedScale,
		};

		if (rafRef.current === null) {
			rafRef.current = requestAnimationFrame(flushDrag);
		}
	}

	function handleOnStopPropagation(e: React.MouseEvent<SVGCircleElement>) {
		e.stopPropagation();
	}

	function handleOnPointerUp() {
		setIsDragging(false);
		viewLayoutRef.current = null;
		setViewLayout(null);

		if (rafRef.current !== null) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		const pending = pendingPosRef.current;
		if (pending) {
			pendingPosRef.current = null;
			context.controller.handleOnDrag(annotationId, kind, pending, index);
		}
	}

	/** Computed */
	const radius = (isDragging ? 12 : 8) / combinedScale;

	/** Render */
	return (
		<Fragment>
			<g>
				<circle
					cx={cx}
					cy={cy}
					r={20 / combinedScale}
					fill="transparent"
					onPointerDown={handleOnPointerDown}
					onPointerMove={handleOnPointerMove}
					onPointerUp={handleOnPointerUp}
					onPointerCancel={handleOnPointerUp}
					/**
					 * A press on a handle still emits a click, which would otherwise bubble to the
					 * annotation and be read as a click on it — selecting or editing after a drag.
					 */
					onClick={handleOnStopPropagation}
					onDoubleClick={handleOnStopPropagation}
					style={{ cursor: "grab", touchAction: "none" }}
				/>
				<circle
					cx={cx}
					cy={cy}
					r={radius}
					fill="white"
					stroke="black"
					strokeWidth={1 / combinedScale}
					pointerEvents="none"
					style={{ transition: "r 150ms ease" }}
				/>
			</g>
			{isDragging && viewLayout && (
				<AnnotationDragHandlePreview
					ref={previewRef}
					imageUrl={imageUrl}
					viewLayout={viewLayout}
				/>
			)}
		</Fragment>
	);
}

export const AnnotationDragHandle = memo(AnnotationDragHandleComponent);
